import {
  TaskType,
  MessageType,
  OperationType,
  type FromWorkerMessage,
  type ToWorkerMessage,
  type FastImportFallbackReason,
} from "Worker/entities";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  detectFileTypeFromFile,
  FileType,
  type DetectFileTypeFromFileResult,
} from "./useWorkerOperation";
import { MAX_CSV_SIZE, MAX_PARQUET_SIZE, SAMPLE_SIZE } from "./constants";
import { formatBytes } from "components/canvas/detailsSide/utils/formatBytes";
import {
  useImportQueueStore,
  setImportItems as setItems,
  patchImportItem as patch,
  type ImportItem,
} from "./importQueueStore";

export type { ImportItem, ImportItemStatus } from "./importQueueStore";
export { useImportQueueStore } from "./importQueueStore";

export const MAX_BATCH_FILES = 50;
/** Window event that brings the canvas overlay back, expanded. */
export const SHOW_IMPORT_OVERLAY = "show-import-overlay";

/** Auto-retries before the row waits for a human. */
const MAX_AUTO_RETRIES = 2;
const RETRY_BACKOFF_MS = [1_000, 3_000];

/** A file whose worker has said nothing for this long is presumed lost —
 *  without it a crashed worker leaves the whole queue waiting forever. The
 *  buffered POST reports nothing between its last chunk and the reply, so a
 *  slow uplink can trip this on a live upload; the transfer reporting its own
 *  bytes (the progress-bar change) is what closes that. */
const SILENCE_TIMEOUT_MS = 5 * 60_000;

let nextId = 1;
let pumping = false;

const queueState = () => useImportQueueStore.getState();

// ---------------------------------------------------------------------------
// Why a file cannot be imported, in the words of the person who dropped it
// ---------------------------------------------------------------------------

const EXTENSION_REASONS: Record<string, string> = {
  xlsx: "Excel files aren't supported — save the sheet as CSV",
  xlsm: "Excel files aren't supported — save the sheet as CSV",
  xls: "Excel files aren't supported — save the sheet as CSV",
  numbers: "Numbers files aren't supported — export as CSV",
  ods: "OpenDocument sheets aren't supported — export as CSV",
  json: "JSON isn't supported — export the rows as CSV",
  ndjson: "JSON isn't supported — export the rows as CSV",
  xml: "XML isn't supported — export the rows as CSV",
  zip: "Archives aren't opened — unzip it and drop the files inside",
  gz: "Archives aren't opened — unzip it and drop the files inside",
  tgz: "Archives aren't opened — unzip it and drop the files inside",
  tar: "Archives aren't opened — unzip it and drop the files inside",
  "7z": "Archives aren't opened — unzip it and drop the files inside",
  rar: "Archives aren't opened — unzip it and drop the files inside",
  pdf: "PDFs hold no table to read — export the data as CSV",
  doc: "Documents aren't data files — export a CSV instead",
  docx: "Documents aren't data files — export a CSV instead",
  avro: "Avro isn't supported — convert to Parquet or CSV",
  orc: "ORC isn't supported — convert to Parquet or CSV",
  feather: "Feather isn't supported — convert to Parquet or CSV",
  arrow: "Arrow files aren't supported — convert to Parquet or CSV",
  png: "Images can't become a table",
  jpg: "Images can't become a table",
  jpeg: "Images can't become a table",
  gif: "Images can't become a table",
  svg: "Images can't become a table",
};

const SQLITE_HINT =
  "SQLite databases import from Connections → Local SQLite, not here";

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}

/** The rejection a person can act on, for every route the detector refuses. */
export function rejectionMessage(
  detected: DetectFileTypeFromFileResult,
  name: string,
): string {
  if (detected.kind === "csv-with-sqlite-magic") {
    return `Named .${extensionOf(name)} but the bytes are a SQLite database — ${SQLITE_HINT}`;
  }
  if (detected.kind === "sqlite-extension-without-magic") {
    return "Not a SQLite database — the file's header says otherwise";
  }
  if (detected.kind === "route" && detected.fileType === FileType.LocalSqlite) {
    return SQLITE_HINT;
  }
  const ext = extensionOf(name);
  return (
    EXTENSION_REASONS[ext] ??
    (ext
      ? `.${ext} files can't be imported — use CSV, TSV or Parquet`
      : "No file extension — rename it to .csv, .tsv or .parquet")
  );
}

/** A size refusal that names the file's own size, not just the ceiling. */
export function sizeRejection(
  size: number,
  fileType: FileType.CSV | FileType.Parquet,
): string | null {
  if (size === 0) return "File is empty";
  const cap = fileType === FileType.CSV ? MAX_CSV_SIZE : MAX_PARQUET_SIZE;
  if (size > cap) {
    const label = fileType === FileType.CSV ? "CSV/TSV" : "Parquet";
    return `${formatBytes(size)} — the ${label} limit is ${formatBytes(cap)}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Which failures are worth trying again
// ---------------------------------------------------------------------------

/**
 * Whether trying again is safe, from what the worker reported rather than from
 * the words it chose.
 *
 * `/tables/ipc/create` mints a table per call and takes no idempotency key, so
 * a retry after the write began makes a second copy and bills its storage
 * again. Classifying by message text could not tell the two apart: a 504 and a
 * dropped connection both read as "network", and both arrive AFTER the body
 * landed — the proxy answers while the Lance write is still running, locally
 * and through ds-proxy in the cloud.
 *
 * So the default is no. Only a refusal the server issued before touching
 * storage — rate limiting, or the pod's upload slot — says otherwise, and only
 * the worker is in a position to know that.
 */
export function isTransientImportError(
  retryable: boolean | undefined,
): boolean {
  return retryable === true;
}

/**
 * Turn a door's raw refusal into something the row can say. A 413 in
 * particular reaches the user as nginx's HTML error page, and it is the one
 * refusal our own size check cannot predict: what the server measures is the
 * Arrow IPC encoding, which for text-heavy data is LARGER than the file the
 * client weighed.
 */
export function readableImportError(
  message: string,
  mayHaveLanded = true,
): string {
  // Before the 413 arm: the org's storage gate refuses with 413 as well, and
  // "split the file" is the wrong advice for a full quota.
  if (
    /storage_quota_exceeded|status 507|insufficient quota|storage/i.test(
      message,
    )
  ) {
    return "Not enough storage left in this organization";
  }
  if (/status 413/i.test(message)) {
    return "Too large for the server once converted — the uploaded form of a CSV is bigger than the file. Split it and import the parts.";
  }
  if (/status 401|status 403|unauthor|forbidden/i.test(message)) {
    return "Not allowed to import into this model";
  }
  // An HTML error page is noise; keep the first line of anything else.
  const firstLine = message.split("\n")[0].trim();
  const readable = /<html/i.test(firstLine) ? "Import failed" : firstLine;
  // Every refusal above is one the server issued instead of writing. What is
  // left can arrive after the table committed — a proxy answering while the
  // Lance write runs on, or a connection dropping once the body was sent — so
  // the row has to say that rather than invite a retry that makes a copy.
  // A refusal of the file itself — parser or decoder said no before a byte
  // was sent — cannot have left a table behind either.
  if (
    !mayHaveLanded ||
    /parse error|encoding error|number of fields/i.test(readable) ||
    /check whether the table arrived/i.test(readable)
  ) {
    return readable;
  }
  return `${readable} — check whether the table arrived before importing it again`;
}

// ---------------------------------------------------------------------------
// One file, start to finish
// ---------------------------------------------------------------------------

const FALLBACK_REASONS: Record<FastImportFallbackReason, string> = {
  inconsistent_rows: "Rows have uneven lengths — choose how to read it",
  encoding_uncertain: "Text encoding unclear — confirm it",
  legacy_encoding: "Not UTF-8 — confirm the text encoding",
  number_format_uncertain: "Decimal separator unclear — pick , or .",
  rounded_identifiers: "Some columns look like rounded identifiers",
};

type Outcome =
  | { kind: "done" }
  | { kind: "needs_settings"; message: string }
  /** No worker to post to. The row goes back to waiting and the pump stops;
   *  the canvas that owns it releases the queue again once it has one. */
  | { kind: "held" }
  /** `terminal` is set where the outcome is produced, for a failure whose
   *  retryability the message cannot express. */
  | {
      kind: "failed";
      message: string;
      terminal?: boolean;
      /** What the worker said about trying again; absent means do not. */
      retryable?: boolean;
    };

/**
 * Resolve when the worker answers for `filename`. The queue runs one file at a
 * time, so the name is enough to tell the answer apart from an unrelated
 * panel import.
 */
function awaitOutcome(
  worker: Worker,
  filename: string,
  onProgress: (percent: number) => void,
): Promise<Outcome> {
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout>;
    let unsubscribe = () => {};
    const settle = (outcome: Outcome) => {
      clearTimeout(timer);
      unsubscribe();
      worker.removeEventListener("message", onMessage);
      resolve(outcome);
    };
    // The canvas terminates its worker on every model change, and the store
    // replaces a crashed one. A listener left on either hears nothing until
    // the silence timeout, with `pumping` true for all of it — no model's
    // queue moves. Nothing can attach a table whose reply is lost, so the
    // outcome is final; the message does not guess which of the two it was.
    unsubscribe = useBoundStore.subscribe((state) => {
      if (state.worker !== worker) {
        settle({
          kind: "failed",
          terminal: true,
          message:
            "Interrupted before the server answered — check whether the table arrived before importing it again",
        });
      }
    });
    const armSilenceTimeout = () => {
      clearTimeout(timer);
      // Never auto-retried: the upload may well have LANDED and only its reply
      // been lost, and re-posting it would make a second copy of the table.
      timer = setTimeout(
        () =>
          settle({
            kind: "failed",
            message:
              "Import timed out — check whether the table arrived before retrying",
            terminal: true,
          }),
        SILENCE_TIMEOUT_MS,
      );
    };
    function onMessage(event: MessageEvent<FromWorkerMessage>) {
      const msg = event.data;
      if ("task" in msg && msg.task === TaskType.DsvFastImport) {
        if (msg.data.filename !== filename) return;
        settle({
          kind: "needs_settings",
          message: FALLBACK_REASONS[msg.data.reason],
        });
        return;
      }
      if (!("operation" in msg) || msg.operation !== OperationType.Upload) {
        return;
      }
      if (msg.filename !== filename) return;
      if (msg.type === MessageType.Progress) {
        onProgress(msg.progress);
        armSilenceTimeout();
        return;
      }
      if (msg.type === MessageType.Status) {
        settle(
          msg.success
            ? { kind: "done" }
            : { kind: "failed", message: msg.message ?? "Import failed" },
        );
        return;
      }
      if (msg.type === MessageType.Error) {
        settle({
          kind: "failed",
          message: msg.message ?? "Import failed",
          retryable: msg.retryable,
        });
      }
    }
    worker.addEventListener("message", onMessage);
    armSilenceTimeout();
  });
}

/** "stop" when the row could not run and was put back; the pump then waits
 *  for the canvas to release the queue again rather than failing the rest. */
async function runItem(item: ImportItem): Promise<"continue" | "stop"> {
  const modelId = item.modelId;
  const detected = await detectFileTypeFromFile(item.file);
  if (detected.kind !== "route" || detected.fileType === FileType.LocalSqlite) {
    patch(item.id, {
      status: "rejected",
      message: rejectionMessage(detected, item.name),
    });
    return "continue";
  }

  const tooBig = sizeRejection(item.size, detected.fileType);
  if (tooBig) {
    patch(item.id, { status: "rejected", message: tooBig });
    return "continue";
  }

  for (let attempt = item.attempts; ; attempt++) {
    patch(item.id, { status: "importing", progress: 0, attempts: attempt + 1 });
    const outcome = await awaitOutcomeFor(item, modelId, detected.fileType);

    if (outcome.kind === "held") {
      patch(item.id, {
        status: "queued",
        progress: undefined,
        attempts: attempt,
      });
      return "stop";
    }
    if (outcome.kind === "done") {
      patch(item.id, {
        status: "done",
        progress: undefined,
        message: undefined,
      });
      return "continue";
    }
    if (outcome.kind === "needs_settings") {
      patch(item.id, {
        status: "needs_settings",
        progress: undefined,
        message: outcome.message,
      });
      return "continue";
    }
    const canRetry =
      attempt < MAX_AUTO_RETRIES &&
      !outcome.terminal &&
      isTransientImportError(outcome.retryable);
    if (!canRetry) {
      patch(item.id, {
        status: "failed",
        progress: undefined,
        message: readableImportError(
          outcome.message,
          outcome.retryable !== true,
        ),
      });
      return "continue";
    }
    // Rendered in place of the percentage; the raw refusal is not for the row.
    patch(item.id, {
      status: "importing",
      progress: undefined,
      message: "Retrying…",
    });
    await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS[attempt]));
  }
}

/** Post the task and wait for its answer. Reading the bytes is part of the
 *  attempt: a retry must re-read, the previous buffer is already transferred. */
async function awaitOutcomeFor(
  item: ImportItem,
  modelId: string,
  fileType: FileType.CSV | FileType.Parquet,
): Promise<Outcome> {
  let bytes: ArrayBuffer;
  let sample: ArrayBuffer | null = null;
  try {
    bytes = await item.file.arrayBuffer();
    if (fileType === FileType.CSV) {
      sample = await item.file.slice(0, SAMPLE_SIZE).arrayBuffer();
    }
  } catch {
    return { kind: "failed", message: "Could not read the file from disk" };
  }
  // Resolved after the reads and posted to without awaiting in between: the
  // store's sender resolves `worker` at send time, so any gap here leaves the
  // listener on one instance and the task on another, and the row waits out the
  // silence timeout for an answer that went to a worker nobody is watching.
  const worker = useBoundStore.getState().worker;
  if (!worker) return { kind: "held" };
  let shown = -1;
  const pending = awaitOutcome(worker, item.name, (p) => {
    if (p === shown) return;
    shown = p;
    patch(item.id, { progress: p });
  });
  // Transferred, not cloned: a copy of a whole file doubles peak memory, and a
  // large import is already carrying one copy in the worker and another in WASM.
  // Nothing below reads these buffers again; a retry re-reads from the File.
  const message: ToWorkerMessage = sample
    ? {
        task: TaskType.DsvFastImport,
        data: {
          sampleBytes: sample,
          fileBytes: bytes,
          modelID: modelId,
          filename: item.name,
          source: "batch",
        },
      }
    : {
        task: TaskType.ParquetImport,
        data: { fileBytes: bytes, modelID: modelId, filename: item.name },
      };
  worker.postMessage(message, sample ? [bytes, sample] : [bytes]);
  return pending;
}

// ---------------------------------------------------------------------------
// The pump
// ---------------------------------------------------------------------------

/** A file handle can go stale between the drop and its turn in the queue —
 *  moved, renamed, or on a volume that was ejected. */
function unreadableFileMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === "NotFoundError") {
    return "File is no longer where it was dropped from";
  }
  const detail = error instanceof Error ? error.message : String(error);
  return `Could not read the file — ${detail}`;
}

async function pump(): Promise<void> {
  if (pumping) return;
  pumping = true;
  let ran = false;
  let held = false;
  try {
    for (;;) {
      const { items, mountedModelId } = queueState();
      // A batch outlives the canvas it was dropped on: the worker is replaced
      // on a model change, and a table is attached by whichever ClientApp is
      // mounted when the reply lands. Only this model's rows may run.
      const next = items.find(
        (i) => i.status === "queued" && i.modelId === mountedModelId,
      );
      if (!next) break;
      ran = true;
      try {
        if ((await runItem(next)) === "stop") {
          held = true;
          break;
        }
      } catch (error) {
        // One file must never take the run down with it. Every expected
        // failure is already a status; reaching here means something threw —
        // a file moved or unmounted since the drop is the common one — and the
        // row that caused it is the only one that stops.
        patch(next.id, {
          status: "failed",
          progress: undefined,
          message: unreadableFileMessage(error),
        });
      }
      announceProgress();
    }
  } finally {
    pumping = false;
  }
  // Nothing ran means the queue holds only other models' files, so there is no
  // run to summarise — and announcing would drop the done rows out of the tally
  // the real run reports when its model comes back on screen.
  // A held stop is a run that has not happened yet, not one with nothing in
  // it: "0 imported" would be wrong, and it would sweep the done rows too.
  if (ran && !held) announceOutcome();
}

/** This canvas's rows: the store also holds other models' held batches and
 *  whatever earlier runs left un-dismissed, and neither is this run. */
export function runOf(
  items: ImportItem[],
  mountedModelId: string | null,
): ImportItem[] {
  return items.filter((i) => i.modelId === mountedModelId && !i.reported);
}

function thisRun(): ImportItem[] {
  const { items, mountedModelId } = queueState();
  return runOf(items, mountedModelId);
}

/** Which file of how many the run is on. Rejected rows never run, so they are
 *  not part of the count; between two files it names the one about to start. */
export function runPosition(run: ImportItem[]): {
  position: number;
  total: number;
  remaining: number;
} {
  const runnable = run.filter((i) => i.status !== "rejected");
  const total = runnable.length;
  const remaining = runnable.filter((i) => i.status === "queued").length;
  const importing = runnable.some((i) => i.status === "importing");
  const position = Math.min(total, total - remaining + (importing ? 0 : 1));
  return { position, total, remaining };
}

function announceProgress(): void {
  const { position, total, remaining } = runPosition(thisRun());
  if (remaining === 0) return;
  // No `progress` argument: the indicator treats a progress value as
  // "persistent" and never fades it, and the pump can stop for a review long
  // before the run is over — the row shows the percentage anyway.
  showNotificationGlobal(`Importing ${position} of ${total} files…`, "default");
}

/** One line at the end, never one per file — the bar shows a single
 *  notification, so a per-file toast in a 20-file batch is just flicker. */
function announceOutcome(): void {
  const items = thisRun();
  if (items.length === 0) return;
  const done = items.filter((i) => i.status === "done").length;
  const review = items.filter((i) => i.status === "needs_settings").length;
  const stuck = items.filter(
    (i) => i.status === "failed" || i.status === "rejected",
  ).length;

  const parts = [`${done} imported`];
  if (review > 0)
    parts.push(`${review} need${review === 1 ? "s" : ""} settings`);
  if (stuck > 0) parts.push(`${stuck} not imported`);

  dropDoneItems();
  // What stays in the list has now been told; the next run's line is about
  // the next run.
  const told = new Set(items.map((i) => i.id));
  setItems((all) =>
    all.map((i) => (told.has(i.id) ? { ...i, reported: true } : i)),
  );

  const showOverlay = () =>
    window.dispatchEvent(new CustomEvent(SHOW_IMPORT_OVERLAY));
  showNotificationGlobal(
    parts.join(" · "),
    stuck > 0 ? "error" : review > 0 ? "warning" : "success",
    review + stuck > 0 ? { label: "Review", onClick: showOverlay } : undefined,
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Queue files for import and start the pump. Returns how many were taken. */
export function enqueueImportFiles(files: File[], modelId: string): number {
  const taken = files.slice(0, MAX_BATCH_FILES);
  const row = (file: File) => ({
    id: nextId++,
    file,
    modelId,
    name: file.name,
    size: file.size,
    attempts: 0,
  });
  setItems((items) => [
    ...items,
    ...taken.map((file) => ({ ...row(file), status: "queued" as const })),
    // A row each, so the list says which files to drop again; a toast fades
    // before anyone can read off 150 names.
    ...files.slice(MAX_BATCH_FILES).map((file) => ({
      ...row(file),
      status: "rejected" as const,
      message: `Past the ${MAX_BATCH_FILES}-file limit for one drop — import it in the next batch`,
    })),
  ]);
  void pump();
  return taken.length;
}

/**
 * Tell the queue which model is on screen; null while no canvas is mounted.
 * Files queued against another model wait rather than running, because their
 * table would be created under the model they were dropped on and attached to
 * the one that is mounted. Reopening that model resumes them.
 */
export function setImportQueueModel(modelId: string | null): void {
  if (queueState().mountedModelId === modelId) return;
  useImportQueueStore.setState({ mountedModelId: modelId });
  if (modelId !== null) void pump();
}

export function retryImportItem(id: number): void {
  // A retried row is this run's again, whatever an earlier summary said of it.
  patch(id, {
    status: "queued",
    message: undefined,
    attempts: 0,
    reported: false,
  });
  void pump();
}

export function dismissImportItem(id: number): void {
  setItems((items) => items.filter((i) => i.id !== id));
}

/** Drops one model's finished rows, the ones needing settings included: the
 *  person chose to let them go, and a file can always be dropped again. */
export function clearSettledImportsFor(modelId: string): void {
  setItems((items) =>
    items.filter(
      (i) =>
        i.modelId !== modelId ||
        i.status === "queued" ||
        i.status === "importing",
    ),
  );
}

/** A row that landed has nothing left to ask of anyone; only the ones still
 *  owed a decision stay. Run's end only — dropping these at a pause would take
 *  them out of the tally the summary line reports. */
function dropDoneItems(): void {
  setItems((items) => items.filter((i) => i.status !== "done"));
}

export function __resetImportQueueForTesting(): void {
  useImportQueueStore.setState({
    items: [],
    mountedModelId: null,
  });
  nextId = 1;
  pumping = false;
}
